import React from "react";

const DomainSummary = ({
	domainData = [],
	activeTab,
	performanceMetric = "viewability",
	isHighPerformanceSet = false,
}) => {
	if (activeTab !== "domains") {
		return null;
	}

	const getPerformanceMetricInfo = (metric) => {
		const metricInfo = {
			viewability: {
				title: "High Viewability Domains",
				description: "Domains optimized for maximum viewability rates",
				icon: "visibility",
				unit: "%",
			},
			ctr: {
				title: "High CTR Domains",
				description: "Domains with exceptional click-through rates",
				icon: "mouse",
				unit: "%",
			},
			vtr: {
				title: "High VTR Domains",
				description: "Domains with superior view-through rates",
				icon: "play_circle",
				unit: "%",
			},
			reach: {
				title: "High Reach Domains",
				description: "Domains with maximum audience reach potential",
				icon: "gps_fixed",
				unit: "M",
			},
			impressions: {
				title: "High Impression Domains",
				description: "Domains delivering maximum impression volume",
				icon: "bar_chart",
				unit: "M",
			},
			engagement: {
				title: "High Engagement Domains",
				description: "Domains with exceptional user engagement rates",
				icon: "favorite",
				unit: "%",
			},
			completion: {
				title: "High Completion Rate Domains",
				description: "Domains with superior video completion rates",
				icon: "check_circle",
				unit: "%",
			},
		};

		return metricInfo[metric] || metricInfo.viewability;
	};

	// ✅ UPDATED: Calculate real metrics from actual API domain data
	const calculateMetrics = () => {
		if (!domainData || domainData.length === 0) {
			return {
				totalMarketRequests: "0M",
				avgViewability: "0%",
				avgCTR: "0%",
				avgVTR: "0%",
				estDailyReach: "0M",
				totalImpressions: "0M",
				totalSpend: "$0",
				avgCPM: "$0.00",
				domainCount: 0,
				primaryMetricValue: "0",
				primaryMetricAvg: "0",
			};
		}

		const formatImpressions = (value) => {
			if (value >= 1000000000) return `${(value / 1000000000).toFixed(1)}B`;
			if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
			if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
			return value.toString();
		};

		const formatCurrency = (value) => {
			if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
			if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
			return `$${value.toFixed(0)}`;
		};

		// Calculate Total Market Requests
		const totalMarketRequests = domainData.reduce((sum, domain) => {
			return sum + (domain.totalCprMarketRequests || 0);
		}, 0);

		// ✅ ADD: Calculate Total Domain Reach
		const totalDomainReach = domainData.reduce((sum, domain) => {
			const reachValue = parseFloat(domain.domainReach || 0);
			return sum + (isNaN(reachValue) ? 0 : reachValue);
		}, 0);

		// ✅ CORRECTED: Calculate Est. Daily Reach properly
		const estDailyReach = totalDomainReach || 0;

		// ... rest of your existing calculations ...
		const totalViewables = domainData.reduce(
			(sum, domain) => sum + (domain.totalViewables || 0),
			0,
		);
		const totalViewabilityMeasures = domainData.reduce(
			(sum, domain) => sum + (domain.totalViewabilityMeasures || 0),
			0,
		);

		const avgViewability =
			totalViewabilityMeasures > 0
				? (totalViewables / totalViewabilityMeasures) * 100
				: 0;

		const totalClicks = domainData.reduce(
			(sum, domain) => sum + (domain.totalCprClicks || 0),
			0,
		);
		const totalImpressionsWithClickMacro = domainData.reduce(
			(sum, domain) => sum + (domain.totalImpressionsWithClickMacro || 0),
			0,
		);

		const avgCTR =
			totalImpressionsWithClickMacro > 0
				? totalClicks / totalImpressionsWithClickMacro
				: 0;

		const totalCompleteFirstOccurrence = domainData.reduce(
			(sum, domain) => sum + (domain.totalCompleteFirstOccurrence || 0),
			0,
		);
		const totalStartFirstOccurrence = domainData.reduce(
			(sum, domain) => sum + (domain.totalStartFirstOccurrence || 0),
			0,
		);

		const avgVTR =
			totalStartFirstOccurrence > 0
				? (totalCompleteFirstOccurrence / totalStartFirstOccurrence) * 100
				: 0;

		const totalImpressions = domainData.reduce((sum, domain) => {
			return sum + (domain.totalImpressions || 0);
		}, 0);

		const totalSpend = domainData.reduce((sum, domain) => {
			const spend = domain.totalCprMarketDemandPartnerSpendInUsd || 0;
			return sum + (isNaN(spend) ? 0 : spend);
		}, 0);

		const avgCPM =
			totalImpressions > 0 ? (totalSpend / totalImpressions) * 1000 : 0;

		const result = {
			totalMarketRequests: formatImpressions(totalMarketRequests),
			avgViewability: `${avgViewability.toFixed(1)}%`,
			avgCTR: `${avgCTR.toFixed(3)}%`,
			avgVTR: `${avgVTR.toFixed(1)}%`,
			estDailyReach: formatImpressions(estDailyReach), // ✅ Now uses correct calculation
			totalImpressions: formatImpressions(totalImpressions),
			totalSpend: formatCurrency(totalSpend),
			avgCPM: `$${avgCPM.toFixed(2)}`,
			domainCount: domainData.length,
		};

		return result;
	};

	const metrics = calculateMetrics();
	const metricInfo = getPerformanceMetricInfo(performanceMetric);

	const MetricCard = ({ icon, label, value, isHighlighted = false }) => (
		<div
			className={`ds-metric-card ${isHighlighted ? "ds-metric-card-highlighted" : ""}`}
		>
			<div
				className={`ds-metric-icon ${isHighlighted ? "ds-metric-icon-highlighted" : ""}`}
			>
				<span className="material-icons">{icon}</span>
			</div>
			<div className="ds-metric-content">
				<div className="ds-metric-label">{label}</div>
				<div className="ds-metric-value">{value}</div>
			</div>
		</div>
	);

	const getHighlightedMetrics = () => {
		if (!isHighPerformanceSet) return {};

		switch (performanceMetric) {
			case "viewability":
				return { viewability: true };
			case "ctr":
				return { ctr: true };
			case "vtr":
				return { vtr: true };
			case "reach":
				return { reach: true, dailyReach: true };
			case "impressions":
				return { impressions: true };
			default:
				return {};
		}
	};

	const highlightedMetrics = getHighlightedMetrics();

	return (
		<>
			<style>
				{`
          /* Import Material Icons font */
          @import url('https://fonts.googleapis.com/icon?family=Material+Icons');

          .ds-container {
            background: white;
            border-radius: 8px;
            padding: 24px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            margin-bottom: 12px;
            border: 1px solid #e5e7eb;
          }

          .ds-header {
            margin-bottom: 20px;
          }

          .ds-title {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 16px;
            font-weight: 600;
            color: #374151;
            margin-bottom: 4px;
          }

          .ds-title .material-icons {
            font-size: 20px;
            color: #6b7280;
          }

          .ds-subtitle {
            font-size: 14px;
            color: #6b7280;
            margin: 0;
          }

          .ds-performance-badge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            background: linear-gradient(135deg, #3b82f6, #1d4ed8);
            color: white;
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 500;
            margin-left: 8px;
          }

          .ds-performance-badge .material-icons {
            font-size: 14px;
          }

          .ds-metrics-grid {
            display: grid;
            grid-template-columns: repeat(6, 1fr);
            gap: 16px;
          }

          .ds-metric-card {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 20px 16px;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            transition: all 0.2s;
            min-width: 0;
          }

          .ds-metric-card:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
            border-color: #d1d5db;
          }

          .ds-metric-card-highlighted {
            background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
            border-color: #3b82f6;
            box-shadow: 0 4px 16px rgba(59, 130, 246, 0.15);
          }

          .ds-metric-card-highlighted:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(59, 130, 246, 0.2);
          }

          .ds-metric-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            border-radius: 8px;
            background: #f9fafb;
            border: 1px solid #e5e7eb;
            margin-bottom: 12px;
          }

          .ds-metric-icon-highlighted {
            background: linear-gradient(135deg, #3b82f6, #1d4ed8);
            border-color: #1d4ed8;
          }

          .ds-metric-icon-highlighted .material-icons {
            color: white !important;
          }

          .ds-metric-icon .material-icons {
            font-size: 20px;
            color: #6b7280;
          }

          .ds-metric-content {
            flex: 1;
            width: 100%;
          }

          .ds-metric-label {
            font-size: 12px !important;
            font-weight: 500 !important;
            color: #6b7280 !important;
            margin-bottom: 8px !important;
            line-height: 1.3 !important;
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            text-align: center !important;
          }

          .ds-metric-card-highlighted .ds-metric-label {
            color: #1e40af !important;
            font-weight: 600 !important;
          }

          .ds-metric-value {
            font-size: 18px !important;
            font-weight: 600 !important;
            color: #111827 !important;
            line-height: 1 !important;
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            text-align: center !important;
          }

          .ds-metric-card-highlighted .ds-metric-value {
            color: #1e40af !important;
            font-weight: 700 !important;
          }

          .ds-empty-state {
            text-align: center;
            padding: 40px 20px;
            color: #6b7280;
          }

          .ds-empty-state .material-icons {
            font-size: 48px;
            margin-bottom: 12px;
            color: #d1d5db;
            display: block;
          }

          /* Mobile Styles */
          @media (max-width: 768px) {
            .ds-metrics-grid {
              grid-template-columns: repeat(3, 1fr);
              gap: 12px;
            }
            
            .ds-metric-card {
              padding: 16px 12px;
            }
            
            .ds-metric-value {
              font-size: 16px !important;
            }

            .ds-metric-label {
              font-size: 11px !important;
            }

            .ds-metric-icon {
              width: 36px;
              height: 36px;
              margin-bottom: 10px;
            }

            .ds-metric-icon .material-icons {
              font-size: 18px;
            }
          }

          @media (max-width: 480px) {
            .ds-container {
              padding: 16px;
            }
            
            .ds-metrics-grid {
              grid-template-columns: repeat(2, 1fr);
              gap: 10px;
            }

            .ds-metric-value {
              font-size: 14px !important;
            }

            .ds-metric-label {
              font-size: 10px !important;
            }
          }
        `}
			</style>

			<div className="ds-container">
				<div className="ds-header">
					<div className="ds-title">
						<span className="material-icons">analytics</span>
						{isHighPerformanceSet
							? metricInfo.title
							: "Current Results Summary"}
						{isHighPerformanceSet && (
							<div className="ds-performance-badge">
								<span className="material-icons">{metricInfo.icon}</span>
								Top {performanceMetric.toUpperCase()}
							</div>
						)}
					</div>
					<p className="ds-subtitle">
						{isHighPerformanceSet
							? `${metricInfo.description} - ${metrics.domainCount} domains`
							: `Real-time metrics for ${metrics.domainCount} domains`}
					</p>
				</div>

				{domainData.length > 0 ? (
					<div className="ds-metrics-grid">
						<MetricCard
							icon="gps_fixed"
							label="Total Market Requests"
							value={metrics.totalMarketRequests} // ✅ FIXED
							isHighlighted={highlightedMetrics.reach}
						/>

						<MetricCard
							icon="visibility"
							label="Avg. Viewability"
							value={metrics.avgViewability}
							isHighlighted={highlightedMetrics.viewability}
						/>

						<MetricCard
							icon="trending_up"
							label="Avg. CTR"
							value={metrics.avgCTR}
							isHighlighted={highlightedMetrics.ctr}
						/>

						<MetricCard
							icon="play_circle"
							label="Avg. VTR"
							value={metrics.avgVTR}
							isHighlighted={highlightedMetrics.vtr}
						/>

						<MetricCard
							icon="timeline"
							label="Est. Daily Reach"
							value={metrics.estDailyReach}
							isHighlighted={highlightedMetrics.dailyReach}
						/>

						<MetricCard
							icon="bar_chart"
							label="Total Impressions"
							value={metrics.totalImpressions}
							isHighlighted={highlightedMetrics.impressions}
						/>
					</div>
				) : (
					<div className="ds-empty-state">
						<span className="material-icons">analytics</span>
						<p>No domain data available for summary</p>
					</div>
				)}
			</div>
		</>
	);
};

export default DomainSummary;
